import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, TrendingUp, TrendingDown } from 'lucide-react';
import { usePriceOracle } from '../providers/PriceOracleProvider';

const LivePriceDisplay = ({ compact = false }) => {
  const { 
    tokenPrice, 
    priceDirection, 
    isLive, 
    formatPrice,
    updateCount,
    priceSource 
  } = usePriceOracle();

  return (
    <motion.div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/60 border ${
        priceDirection === 'up' 
          ? 'border-green-500/70 shadow-green-500/30 shadow-lg' 
          : priceDirection === 'down'
            ? 'border-red-500/70 shadow-red-500/30 shadow-lg'
            : 'border-cyan-500/50'
      } transition-all duration-300`}
      animate={priceDirection ? { scale: [1, 1.05, 1] } : {}}
      transition={{ duration: 0.3 }}
      data-testid="live-price-display"
    >
      {/* Live Indicator */}
      <div className="flex items-center gap-1">
        <motion.div
          className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-400' : 'bg-yellow-400'}`}
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
        <Zap className="w-3 h-3 text-yellow-400" />
        <span className={`text-[10px] font-bold uppercase ${isLive ? 'text-green-400' : 'text-yellow-400'}`}>
          {isLive ? 'LIVE' : 'FALLBACK'}
        </span>
      </div>

      {/* Price Display */}
      <div className="flex items-center gap-1">
        <span className="text-white/60 text-xs">$SLAP:</span>
        <AnimatePresence mode="wait">
          <motion.span
            key={updateCount}
            className={`font-mono font-bold text-sm ${
              priceDirection === 'up' 
                ? 'text-green-400' 
                : priceDirection === 'down'
                  ? 'text-red-400'
                  : 'text-cyan-400'
            }`}
            initial={{ opacity: 0, y: priceDirection === 'up' ? 10 : -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: priceDirection === 'up' ? -10 : 10 }}
            transition={{ duration: 0.2 }}
          >
            {formatPrice(tokenPrice)}
          </motion.span>
        </AnimatePresence>

        {/* Direction Arrow */}
        {priceDirection && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
          >
            {priceDirection === 'up' ? (
              <TrendingUp className="w-4 h-4 text-green-400" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-400" />
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default LivePriceDisplay;
